import * as React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@/elements/Button";
import { withOpeningReset } from "@/utils/withOpeningReset";
import {
  FiltrazioneDto,
  getGetNodoPortaleQueryKey,
  getGetNodoQueryKey,
  NodoDto,
  useUpdateFiltrazioni,
} from "@/api";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormHelperText, InputLabel } from "@mui/material";
import { SmallLoading } from "../Loading";
import { useQueryClient } from "@tanstack/react-query";
import { DateTimeField } from "@mui/x-date-pickers";
import moment from "moment/moment";
import FileField from "../FileField";
import { portaleConfig } from "@/portaliConfig";

function FiltrazioneDialog({
  open = false,
  onClose,
  nodo,
  filtrazione,
}: {
  open?: boolean;
  onClose?: () => void;
  nodo: NodoDto;
  filtrazione?: Partial<FiltrazioneDto> | null;
}) {
  const { t } = useTranslation();

  const handleClose = () => {
    onClose?.();
  };

  const {
    handleSubmit,
    formState: { isValid },
    control,
    setValue,
    resetField,
  } = useForm<FiltrazioneDto>({
    defaultValues: filtrazione || {},
  });

  const { mutateAsync, isLoading } = useUpdateFiltrazioni();

  const queryClient = useQueryClient();

  const onSubmit = async (data: FiltrazioneDto) => {
    try {
      await mutateAsync({
        id: nodo.id,
        data: [
          {
            id: filtrazione?.id,
            changeType: filtrazione?.id ? "updated" : "added",
            entity: data,
          },
        ],
      });
      await queryClient.invalidateQueries({
        queryKey: getGetNodoPortaleQueryKey(portaleConfig.id, nodo.id),
      });
      onClose?.();
    } catch (err) {}
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {filtrazione?.id
          ? t("filtrazioneDialog.titolo.modifica")
          : t("filtrazioneDialog.titolo.crea")}
      </DialogTitle>
      <DialogContent>
        <InputLabel htmlFor="crea-filtrazione-data">
          {t("filtrazioneDialog.campi.data")}
        </InputLabel>
        <Controller
          name="data"
          control={control}
          rules={{ required: t("filtrazioneDialog.errori.data") }}
          render={({ field, fieldState: { error } }) => {
            const { value, name, onBlur, onChange, ref } = field;
            const m =
              typeof value === "string"
                ? moment(value)
                : moment.isMoment(value)
                ? value
                : null;
            return (
              <>
                <DateTimeField
                  ref={ref}
                  format="DD/MM/YYYY"
                  name={name}
                  onBlur={onBlur}
                  onChange={(value) => onChange(value?.format("YYYY-MM-DD"))}
                  value={m}
                  fullWidth
                />
                {error && (
                  <FormHelperText error>{error?.message}</FormHelperText>
                )}
              </>
            );
          }}
        />

        <InputLabel>{t("filtrazioneDialog.campi.rapportino")}</InputLabel>
        <Controller
          name="fileRapportino"
          control={control}
          rules={{ required: t("filtrazioneDialog.errori.file") }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <>
              <FileField
                value={value}
                onChange={(f) => {
                  onChange(f);
                  if (f?.id) {
                    setValue("fileRapportinoId", f?.id);
                  } else {
                    resetField("fileRapportinoId");
                  }
                }}
              />
              {error && <FormHelperText error>{error?.message}</FormHelperText>}
            </>
          )}
        />
      </DialogContent>
      <DialogActions>
        {isLoading && <SmallLoading sx={{ mr: 1 }} />}
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={!isValid || isLoading}
          onClick={handleSubmit(onSubmit)}
        >
          {filtrazione?.id
            ? t("filtrazioneDialog.azioni.salva")
            : t("filtrazioneDialog.azioni.crea")}
        </Button>
        <Button
          variant="contained"
          color="light"
          disabled={isLoading}
          onClick={handleClose}
        >
          {t("azioni.annulla")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default withOpeningReset(FiltrazioneDialog);
